/**
 * useWojChat - Hook for Woj Basketball Scouting AI
 *
 * Manages chat state, deep research tasks, and player evaluations
 * using the Gemini-powered Woj backend API.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// API base URL - uses environment variable or defaults to local backend
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001';

// Max time to poll before showing timeout (8 minutes)
const MAX_RESEARCH_TIMEOUT = 8 * 60 * 1000;

/**
 * Custom hook for Woj chat functionality
 * @returns {Object} Chat state and action functions
 */
export const useWojChat = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeResearch, setActiveResearch] = useState(null);
  const [activeExaSearch, setActiveExaSearch] = useState(null);
  const [error, setError] = useState(null);
  const pollTimeoutRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Build conversation history for API calls
   */
  const buildHistory = useCallback(() => {
    return messages
      .filter(m => !m.isResearch) // Exclude research messages from regular chat history
      .map(m => ({
        role: m.role === 'user' ? 'user' : 'model',
        content: m.content
      }));
  }, [messages]);

  /**
   * Send a regular chat message to Woj
   * @param {string} content - The message content
   */
  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/woj/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          history: buildHistory()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('[WojChat] Error:', err);
      setError(err.message || 'Failed to send message');

      // Add error message to chat
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `⚠️ Error: ${err.message || 'Failed to connect to Woj. Please try again.'}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, buildHistory]);

  /**
   * Start a deep research task
   * @param {string} query - The research query
   * @param {string} context - Additional context
   */
  const startResearch = useCallback(async (query, context = '') => {
    if (!query.trim() || isLoading) return;

    const userMessage = {
      id: `research-${Date.now()}`,
      role: 'user',
      content: `🔬 **Deep Research Request**\n\n${query}`,
      timestamp: new Date().toISOString(),
      isResearch: true
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/woj/research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, context })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      setActiveResearch({
        interactionId: data.interactionId,
        status: data.status
      });

      // Add placeholder message for research result
      const researchMessage = {
        id: `research-result-${data.interactionId}`,
        role: 'assistant',
        content: '🔬 **Deep research in progress...** (0s)\n\nI\'m conducting comprehensive research using multiple sources. This typically takes 2-5 minutes.',
        timestamp: new Date().toISOString(),
        isResearch: true,
        researchId: data.interactionId,
        researchStatus: 'in_progress',
        originalQuery: query,
        originalContext: context
      };

      setMessages(prev => [...prev, researchMessage]);

      // Start polling for results
      pollForResearchResults(data.interactionId);

    } catch (err) {
      console.error('[WojChat] Research error:', err);
      setError(err.message || 'Failed to start research');
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  /**
   * Poll for research results with exponential backoff
   * @param {string} interactionId - The research interaction ID
   */
  const pollForResearchResults = useCallback((interactionId) => {
    const startTimestamp = Date.now();
    let consecutiveErrors = 0;

    const updateElapsedTime = () => {
      const elapsed = Math.floor((Date.now() - startTimestamp) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    };

    const poll = async () => {
      // Check max timeout
      if (Date.now() - startTimestamp > MAX_RESEARCH_TIMEOUT) {
        setMessages(prev => prev.map(m =>
          m.researchId === interactionId
            ? { ...m, content: '⏱️ **Research timed out**\n\nThe deep research is taking longer than expected. You can try again with a more specific query.', researchStatus: 'timeout', canRetry: true }
            : m
        ));
        setActiveResearch(null);
        setIsLoading(false);
        pollTimeoutRef.current = null;
        return false;
      }

      try {
        const response = await fetch(`${API_BASE}/api/woj/research/${interactionId}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        consecutiveErrors = 0;

        if (data.status === 'completed') {
          setMessages(prev => prev.map(m =>
            m.researchId === interactionId
              ? { ...m, content: data.result || 'Research completed but no results returned.', researchStatus: 'completed' }
              : m
          ));
          setActiveResearch(null);
          setIsLoading(false);
          pollTimeoutRef.current = null;
          return false;
        } else if (data.status === 'failed') {
          setMessages(prev => prev.map(m =>
            m.researchId === interactionId
              ? { ...m, content: `❌ **Research failed:** ${data.error || 'Unknown error occurred'}`, researchStatus: 'failed', canRetry: true }
              : m
          ));
          setActiveResearch(null);
          setIsLoading(false);
          pollTimeoutRef.current = null;
          return false;
        }

        // Still in progress — update elapsed time
        const timeStr = updateElapsedTime();
        setMessages(prev => prev.map(m =>
          m.researchId === interactionId && m.researchStatus === 'in_progress'
            ? { ...m, content: `🔬 **Deep research in progress...** (${timeStr})\n\nI'm conducting comprehensive research using multiple sources. This typically takes 2-5 minutes.` }
            : m
        ));
        return true;
      } catch (err) {
        consecutiveErrors++;
        console.warn(`[useWojChat] Poll error (attempt ${consecutiveErrors}):`, err.message);
        if (consecutiveErrors >= 3) {
          setMessages(prev => prev.map(m =>
            m.researchId === interactionId
              ? { ...m, content: `⚠️ **Connection lost**\n\nUnable to check research status. The research may still be running.\n\nError: ${err.message}`, researchStatus: 'error', canRetry: true }
              : m
          ));
          setActiveResearch(null);
          setIsLoading(false);
          pollTimeoutRef.current = null;
          return false;
        }
        return true;
      }
    };

    const pollWithBackoff = (attempt = 0) => {
      const interval = attempt < 3 ? 3000 : attempt < 6 ? 5000 : attempt < 10 ? 10000 : 15000;
      pollTimeoutRef.current = setTimeout(async () => {
        const shouldContinue = await poll();
        if (shouldContinue) {
          pollWithBackoff(attempt + 1);
        }
      }, interval);
    };

    // Immediate first check, then start backoff polling
    poll().then(shouldContinue => {
      if (shouldContinue) {
        pollWithBackoff(0);
      }
    });
  }, []);

  /**
   * Evaluate a player using Woj's framework
   * @param {string} playerName - The player's name
   * @param {Object} stats - Optional stats object
   * @param {string} context - Additional context
   */
  const evaluatePlayer = useCallback(async (playerName, stats = null, context = '') => {
    if (!playerName.trim() || isLoading) return;

    const userMessage = {
      id: `eval-${Date.now()}`,
      role: 'user',
      content: `🏀 **Player Evaluation Request**\n\nEvaluate: **${playerName}**`,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/woj/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerName, stats, context })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const evalMessage = {
        id: `eval-result-${Date.now()}`,
        role: 'assistant',
        content: data.evaluation,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, evalMessage]);
    } catch (err) {
      console.error('[WojChat] Evaluation error:', err);
      setError(err.message || 'Failed to evaluate player');

      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `⚠️ Error evaluating ${playerName}: ${err.message || 'Please try again.'}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  /**
   * Search for player scoops/socials using Exa
   * @param {string} query - Search query (player name, topic)
   * @param {string} searchType - Type: 'scoop', 'socials', 'news', 'stats', 'evaluation', 'nil', 'general'
   */
  const searchScoop = useCallback(async (query, searchType = 'scoop') => {
    if (!query.trim() || isLoading) return;

    const searchTypeLabels = {
      scoop: 'Breaking News & Scoops',
      socials: 'Social Media',
      news: 'Recent News',
      stats: 'Stats & Analytics',
      evaluation: 'Scouting Reports',
      nil: 'NIL & Valuation',
      general: 'General Search'
    };

    const userMessage = {
      id: `scoop-${Date.now()}`,
      role: 'user',
      content: `🔍 **Exa Search: ${searchTypeLabels[searchType]}**\n\nSearching: **${query}**`,
      timestamp: new Date().toISOString(),
      isExaSearch: true
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setActiveExaSearch({ query, searchType });
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/woj/scoop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, searchType, numResults: 10 })
      });

      const data = await response.json();

      // Handle non-OK responses that have JSON error data
      if (!response.ok && !data.isConfigError) {
        throw new Error(data.details || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle config error specially
      if (data.isConfigError) {
        const configMessage = {
          id: `config-error-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ **Exa API Key Required**\n\n${data.setupInstructions || 'Please configure your EXA_API_KEY in backend/.env'}\n\nGet your free API key at: [dashboard.exa.ai](https://dashboard.exa.ai/api-keys)`,
          timestamp: new Date().toISOString(),
          isExaSearch: true,
          isConfigError: true
        };
        setMessages(prev => [...prev, configMessage]);
        setIsLoading(false);
        setActiveExaSearch(null);
        return;
      }

      // Build response content
      let content = '';

      if (data.wojAnalysis) {
        content = data.wojAnalysis;
        content += '\n\n---\n\n';
      }

      if (data.markdown) {
        content += data.markdown;
      } else if (data.results && data.results.length > 0) {
        content += `## Search Results for "${query}"\n\n`;
        data.results.forEach((result, idx) => {
          content += `### ${idx + 1}. ${result.title}\n`;
          content += `🔗 [Link](${result.url})\n`;
          if (result.summary) content += `> ${result.summary}\n`;
          content += '\n';
        });
      } else {
        content = `No results found for "${query}". Try a different search term or search type.`;
      }

      const resultMessage = {
        id: `scoop-result-${Date.now()}`,
        role: 'assistant',
        content: content,
        timestamp: new Date().toISOString(),
        isExaSearch: true,
        exaResults: data.results,
        searchType
      };

      setMessages(prev => [...prev, resultMessage]);

    } catch (err) {
      console.error('[WojChat] Scoop error:', err);
      setError(err.message || 'Failed to search');

      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `⚠️ Search failed: ${err.message || 'Please try again.'}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
      setActiveExaSearch(null);
    }
  }, [isLoading]);

  /**
   * Run comprehensive Exa search for a player
   * @param {string} playerName - The player's name
   * @param {string[]} searchTypes - Types to search
   */
  const comprehensiveSearch = useCallback(async (playerName, searchTypes = ['scoop', 'stats', 'socials']) => {
    if (!playerName.trim() || isLoading) return;

    const userMessage = {
      id: `comprehensive-${Date.now()}`,
      role: 'user',
      content: `🔬 **Comprehensive Intel Search**\n\nPlayer: **${playerName}**\nSearching: ${searchTypes.join(', ')}`,
      timestamp: new Date().toISOString(),
      isExaSearch: true,
      isComprehensive: true
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setActiveExaSearch({ playerName, comprehensive: true });
    setError(null);

    // Add placeholder
    const placeholderMessage = {
      id: `comprehensive-loading-${Date.now()}`,
      role: 'assistant',
      content: `🔍 **Gathering intel on ${playerName}...**\n\nRunning comprehensive Exa searches:\n${searchTypes.map(t => `- ${t}`).join('\n')}\n\n*"No stone unturned."*`,
      timestamp: new Date().toISOString(),
      isExaSearch: true,
      isLoading: true
    };
    setMessages(prev => [...prev, placeholderMessage]);

    try {
      const response = await fetch(`${API_BASE}/api/woj/comprehensive-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerName, searchTypes })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Build comprehensive response
      let content = '';

      if (data.wojReport) {
        content = data.wojReport;
      } else {
        content = `## Intel Report: ${playerName}\n\n`;
        Object.entries(data.results || {}).forEach(([type, result]) => {
          if (result.markdown) {
            content += `### ${type.toUpperCase()}\n${result.markdown}\n\n`;
          }
        });
      }

      // Update the placeholder message
      setMessages(prev => prev.map(m =>
        m.id === placeholderMessage.id
          ? {
              ...m,
              content: content || `Research complete for ${playerName}, but no significant results found.`,
              isLoading: false,
              comprehensiveResults: data.results
            }
          : m
      ));

    } catch (err) {
      console.error('[WojChat] Comprehensive search error:', err);
      setError(err.message || 'Failed to search');

      // Update placeholder with error
      setMessages(prev => prev.map(m =>
        m.id === placeholderMessage.id
          ? {
              ...m,
              content: `⚠️ Search failed: ${err.message || 'Please try again.'}`,
              isLoading: false,
              isError: true
            }
          : m
      ));
    } finally {
      setIsLoading(false);
      setActiveExaSearch(null);
    }
  }, [isLoading]);

  /**
   * Retry a failed/timed-out research request
   * @param {string} originalQuery - The original research query
   * @param {string} originalContext - The original context
   */
  const retryResearch = useCallback(async (originalQuery, originalContext = '') => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    await startResearch(originalQuery, originalContext);
  }, [startResearch]);

  /**
   * Clear chat history
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setActiveResearch(null);
    setActiveExaSearch(null);
    setError(null);
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  return {
    messages,
    isLoading,
    activeResearch,
    activeExaSearch,
    error,
    sendMessage,
    startResearch,
    retryResearch,
    evaluatePlayer,
    searchScoop,
    comprehensiveSearch,
    clearChat
  };
};

export default useWojChat;
